"use client"

import { useEffect, useState, useRef } from "react"
import { ThinkingVisual } from "@/components/ui/thinking-visual"
import type { ThinkingConfig } from "@/lib/logosos/ui/thinking"

interface AssistantThinkingRowProps {
  onTransitionComplete?: () => void
  isExternalOperation?: boolean
}

/**
 * AssistantThinkingRow Component
 *
 * Renders a transient thinking indicator during advisor deliberation.
 * - Appears only after user sends message and system is truly thinking
 * - Uses calm motion by default, escalates to urgent after 2.0s
 * - Dissolves into response with fade transition (no layout shift)
 * - Never persists in transcript
 */
export function AssistantThinkingRow({ onTransitionComplete, isExternalOperation = false }: AssistantThinkingRowProps) {
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [breathMode, setBreathMode] = useState<"calm" | "urgent">("calm")
  const [operationLabel, setOperationLabel] = useState<string | null>(null)
  const startTimeRef = useRef(Date.now())
  const escalationTimerRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    console.log("[v0] AssistantThinkingRow mounted, starting calm motion")

    if (isExternalOperation) {
      console.log("[v0] External operation detected, escalating to urgent immediately")
      setBreathMode("urgent")
      setOperationLabel("Gathering external context")
    } else {
      // Start escalation timer
      escalationTimerRef.current = setTimeout(() => {
        const elapsed = Date.now() - startTimeRef.current
        console.log("[v0] Escalating to urgent motion after", elapsed, "ms")
        setBreathMode("urgent")
      }, 2000)
    }

    return () => {
      if (escalationTimerRef.current) {
        clearTimeout(escalationTimerRef.current)
      }
    }
  }, [isExternalOperation])
  // </CHANGE>

  useEffect(() => {
    if (onTransitionComplete) {
      // This effect runs when the component should start fading out
      // The parent will call this when first token arrives
    }
  }, [onTransitionComplete])
  // </CHANGE>

  const config: ThinkingConfig = {
    active: true,
    breathMode,
  }

  return (
    <div
      className={`flex items-start gap-3 mb-6 transition-opacity duration-150 ${isFadingOut ? "opacity-0" : "opacity-100"}`}
      data-thinking-row="true"
      data-breath-mode={breathMode}
      aria-live="polite"
      aria-label="Advisor is thinking"
    >
      <div className="flex-shrink-0">
        <ThinkingVisual config={config} context="inline" className="w-10 h-10" />
      </div>
      {/* </CHANGE> */}

      {operationLabel && (
        <div className="flex items-center h-10">
          <span className="text-xs text-slate-500">{operationLabel}</span>
        </div>
      )}
      {/* </CHANGE> */}
    </div>
  )
}
